import React, { useState } from "react";
import { motion } from "framer-motion";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, LoadingState } from "@/components/app/primitives";

const nodeTone = (s) => s === "complete" ? { ring: "border-[--green] bg-[--green-soft]", text: "text-[--green]", chip: "validated", label: "Complete" }
  : s === "current" ? { ring: "border-[--six-crimson] bg-[--six-crimson-soft]", text: "text-[#E86A6A]", chip: "crimson", label: "In progress" }
  : { ring: "border-[--hairline] bg-[--shell-2]", text: "text-[--tx-3]", chip: "neutral", label: "Upcoming" };

export default function ImpactEnginePage() {
  const { data: timeline, loading } = useFetch("/timeline");
  const [selectedDay, setSelectedDay] = useState(null);

  if (loading) return <LoadingState message="Loading the 30-day cadence..." />;
  const selected = (timeline || []).find((t) => t.id === selectedDay) || (timeline || []).find((t) => t.status === "current") || timeline?.[0];

  return (
    <div data-testid="impact-engine-page">
      <PageHeader
        kicker="Scene 9 - 30-day impact engine"
        title="30-Day Impact Engine"
        subtitle="The living operating cadence from mobilization to proof report. The timeline updates with evidence readiness - decisions gate launches, and leading indicators carry the story until holdout reads mature."
      />

      {/* Milestone rail */}
      <div className="relative mb-6" data-testid="timeline-rail">
        <div className="absolute left-0 right-0 top-[22px] h-px bg-[--divider]" />
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 relative">
          {(timeline || []).map((t) => {
            const tone = nodeTone(t.status);
            const isSelected = selected?.id === t.id;
            return (
              <button key={t.id} onClick={() => setSelectedDay(t.id)} data-testid={`timeline-node-${t.id}`}
                className="flex flex-col items-center gap-1.5 group">
                <div className={`w-11 h-11 rounded-full border-2 ${tone.ring} ${isSelected ? "ring-2 ring-[--ring]" : ""} flex items-center justify-center font-display text-xs font-bold ${tone.text} transition-all group-hover:scale-105 bg-[--shell-1]`}>
                  D{t.day}
                </div>
                <span className={`text-[10px] font-medium leading-tight text-center ${isSelected ? "text-[--tx-1]" : "text-[--tx-3]"}`}>{t.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <motion.div key={selected.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.16 }}>
          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title={`Day ${selected.day} - ${selected.title}`} className="lg:col-span-2" testId="timeline-detail-panel"
              right={<StatusChip tone={nodeTone(selected.status).chip}>{nodeTone(selected.status).label}</StatusChip>}>
              <ul className="space-y-2">
                {selected.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px] text-[--tx-2] leading-relaxed">
                    <span className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${selected.status === "complete" ? "bg-[--green]" : selected.status === "current" ? "bg-[#E86A6A]" : "bg-[--tx-3]"}`} />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-3 border-t border-[--divider] text-xs text-[--tx-3]">{selected.evidence_note}</div>
            </Panel>

            <div className="space-y-4">
              <Panel title="Decision points" testId="timeline-decisions-panel">
                {selected.decision_points?.length ? (
                  <div className="space-y-2">
                    {selected.decision_points.map((d) => (
                      <div key={d} className="rounded-lg border border-[--amber] bg-[--amber-soft] px-3 py-2 text-xs font-medium text-[--tx-1]">{d}</div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[--tx-3]">No gating decisions at this milestone.</p>
                )}
              </Panel>
              <Panel title="Operating rhythm" testId="timeline-rhythm-panel">
                <div className="space-y-2 text-xs text-[--tx-2]">
                  <div className="flex gap-2"><span className="font-mono text-[10px] uppercase text-[#E86A6A] w-14 pt-px shrink-0">Daily</span>20-min stuck-order triage; save pod list drop 8am; fallout clearance check</div>
                  <div className="flex gap-2"><span className="font-mono text-[10px] uppercase text-[--ai] w-14 pt-px shrink-0">Weekly</span>Command center review; sponsor update Mondays; huddle cards refreshed; risk review</div>
                  <div className="flex gap-2"><span className="font-mono text-[10px] uppercase text-[--green] w-14 pt-px shrink-0">Gates</span>Day 7 cohorts/decisions · Day 10 launch · Day 15 pulse · Day 20 scale/stop · Day 30 proof</div>
                </div>
              </Panel>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
